import { useForm } from "react-hook-form";
import { useUsers } from "@/lib/api/user.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { PlusIcon } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form.tsx";
import { CheckIcon, ReloadIcon } from "@radix-ui/react-icons";
import {
  CreateMentorshipInput,
  createMentorshipSchema,
  useCreateMentorship,
} from "@/lib/api/mentorships.ts";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";

interface AddMentorshipProps {
  userId: string;
  isMentor: boolean;
  isMentee: boolean;
  disabled?: boolean;
}

export function AddMentorship({
  userId,
  isMentor,
  isMentee,
  disabled,
}: AddMentorshipProps) {
  const { data: mentors } = useUsers(
    1,
    1000,
    undefined,
    "MENTOR",
    undefined,
    undefined,
    undefined,
    isMentee,
    false,
  );

  const { data: mentees } = useUsers(
    1,
    1000,
    undefined,
    "MENTEE",
    undefined,
    undefined,
    undefined,
    isMentor,
    false,
  );

  const { mutateAsync: createMentorship, isPending } = useCreateMentorship();
  const [open, setOpen] = useState(false);
  const [mentorSearch, setMentorSearch] = useState("");
  const [menteeSearch, setMenteeSearch] = useState("");

  const form = useForm<CreateMentorshipInput>({
    resolver: zodResolver(createMentorshipSchema),
    defaultValues: {
      mentorId: isMentor ? userId : undefined,
      menteeId: isMentee ? userId : undefined,
    },
  });

  async function onSubmit(input: CreateMentorshipInput) {
    await createMentorship(input);

    form.reset();
    setOpen(false);
  }

  useEffect(() => {
    if (!open) {
      form.reset();
      setMentorSearch("");
      setMenteeSearch("");
    }
  }, [open]);

  const filteredMentors = mentors?.users
    ?.filter((mentor) => !mentor.deactivated)
    .filter((mentor) =>
      mentor.name?.toLowerCase().includes(mentorSearch.toLowerCase()),
    );

  const filteredMentees = mentees?.users
    ?.filter((mentee) => !mentee.deactivated)
    .filter((mentee) =>
      mentee.name?.toLowerCase().includes(menteeSearch.toLowerCase()),
    );

  const MentorField = (
    <FormField
      control={form.control}
      name="mentorId"
      render={({ field }) => (
        <FormItem>
          <Select onValueChange={field.onChange} defaultValue={field.value}>
            <FormControl>
              <SelectTrigger className="rounded-xl capitalize">
                <SelectValue placeholder="Select a mentor" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <div className="px-2 pb-2">
                <Input
                  placeholder="Search mentors..."
                  value={mentorSearch}
                  onChange={(e) => setMentorSearch(e.target.value)}
                  className="h-8"
                />
              </div>
              <SelectGroup>
                {filteredMentors?.map((mentor) => (
                  <SelectItem
                    key={mentor.id}
                    value={mentor.id}
                    className="capitalize"
                  >
                    {mentor.name || mentor.first_name || mentor.phone}
                  </SelectItem>
                ))}
                {filteredMentors?.length === 0 && (
                  <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                    No mentors found
                  </div>
                )}
              </SelectGroup>
            </SelectContent>
          </Select>
          <FormMessage className="text-xs" />
        </FormItem>
      )}
    />
  );

  const MenteeField = (
    <FormField
      control={form.control}
      name="menteeId"
      render={({ field }) => (
        <FormItem>
          <Select onValueChange={field.onChange} defaultValue={field.value}>
            <FormControl>
              <SelectTrigger className="rounded-xl capitalize">
                <SelectValue placeholder="Select a mentee" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <div className="px-2 pb-2">
                <Input
                  placeholder="Search mentees..."
                  value={menteeSearch}
                  onChange={(e) => setMenteeSearch(e.target.value)}
                  className="h-8"
                />
              </div>
              <SelectGroup>
                {filteredMentees?.map((mentee) => (
                  <SelectItem
                    key={mentee.id}
                    value={mentee.id}
                    className="capitalize"
                  >
                    {mentee.name || mentee.first_name || mentee.phone}
                  </SelectItem>
                ))}
                {filteredMentees?.length === 0 && (
                  <div className="px-2 py-6 text-center text-sm text-muted-foreground">
                    No mentees found
                  </div>
                )}
              </SelectGroup>
            </SelectContent>
          </Select>
          <FormMessage className="text-xs" />
        </FormItem>
      )}
    />
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="default"
          className="bg-black hover:bg-gray-700"
          disabled={disabled}
        >
          <PlusIcon className="mr-2 size-4" aria-hidden="true" />
          Add Mentorship
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Mentorship</DialogTitle>
          <DialogDescription>
            Add a new mentorship between a mentor and a mentee
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            {isMentee ? MentorField : MenteeField}

            <DialogFooter className="gap-2 pt-2 sm:space-x-0">
              <Button disabled={isPending}>
                {isPending ? (
                  <ReloadIcon
                    className="mr-2 size-4 animate-spin"
                    aria-hidden="true"
                  />
                ) : (
                  <CheckIcon className="mr-2 size-4" aria-hidden="true" />
                )}
                {isPending ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
